(() => {
  if (customElements.get('bestseller-carousel')) return;

  const moneyFormat = window.theme?.moneyFormat || '${{amount}}';

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, moneyFormat);
    }
    return (cents / 100).toFixed(2);
  }

  const DESKTOP_MQ = '(min-width: 750px)';

  class BestsellerCarousel extends HTMLElement {
    constructor() {
      super();
      this._onResize = this.debounce(() => this.syncCarouselMode(), 150);
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.layoutMobile = this.dataset.layoutMobile || this.dataset.layout || 'carousel';
      this.layoutDesktop = this.dataset.layoutDesktop || this.dataset.layout || 'carousel';
      this.viewport = this.querySelector('[data-bsc-viewport]');
      this.track = this.querySelector('[data-bsc-track]');
      this.prevBtn = this.querySelector('[data-bsc-prev]');
      this.nextBtn = this.querySelector('[data-bsc-next]');

      if (!this.viewport || !this.track) return;

      this.originalSlides = Array.from(this.track.children);
      this.dragEnabled = this.dataset.drag === 'true';
      this.infinite = this.dataset.infinite === 'true';
      this.cloneCount = 0;
      this._carouselActive = false;
      this._carouselBound = false;

      this.bindCards();
      this.bindAddToCart();

      if (!this.needsCarouselJs()) return;

      this.bindCarouselInteractions();
      window.addEventListener('resize', this._onResize);
      requestAnimationFrame(() => this.syncCarouselMode(true));
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
    }

    needsCarouselJs() {
      return this.layoutMobile === 'carousel' || this.layoutDesktop === 'carousel';
    }

    isDesktop() {
      return window.matchMedia(DESKTOP_MQ).matches;
    }

    getActiveLayout() {
      return this.isDesktop() ? this.layoutDesktop : this.layoutMobile;
    }

    isCarouselActive() {
      return this.getActiveLayout() === 'carousel';
    }

    bindCarouselInteractions() {
      if (this._carouselBound) return;
      this._carouselBound = true;

      this.bindArrows();
      if (this.dragEnabled) this.bindDrag();
      this.bindScroll();
    }

    syncCarouselMode(instant = false) {
      if (!this.needsCarouselJs()) return;

      const shouldBeActive = this.isCarouselActive();

      if (shouldBeActive && !this._carouselActive) {
        this.enableCarouselMode(instant);
      } else if (!shouldBeActive && this._carouselActive) {
        this.disableCarouselMode();
      } else if (shouldBeActive) {
        this.recenter(instant);
        this.updateArrowState();
      }
    }

    enableCarouselMode(instant = false) {
      this._carouselActive = true;

      if (this.infinite && this.originalSlides.length > 0 && !this.cloneCount) {
        this.setupInfiniteClones();
      }

      this.recenter(instant);
      this.updateArrowState();
    }

    disableCarouselMode() {
      this._carouselActive = false;
      this.removeClones();
      this.viewport.scrollLeft = 0;
      this.viewport.classList.remove('is-dragging', 'was-dragging');
    }

    removeClones() {
      if (!this.cloneCount) return;
      this.track.querySelectorAll('[data-clone="true"]').forEach((node) => node.remove());
      this.cloneCount = 0;
    }

    bindCards() {
      this.querySelectorAll('[data-bsc-card]').forEach((card) => {
        this.initCard(card);
      });
    }

    initCard(card) {
      const variantsEl = card.querySelector('[data-bsc-variants]');
      const mediaEl = card.querySelector('[data-bsc-media]');
      const variantsUi = card.querySelector('[data-bsc-variants-ui]');

      if (!variantsEl) return;

      let variants;
      let mediaMap = {};

      try {
        variants = JSON.parse(variantsEl.textContent);
      } catch (_) {
        return;
      }

      if (mediaEl) {
        try {
          const mediaList = JSON.parse(mediaEl.textContent);
          mediaList.forEach((m) => {
            mediaMap[m.id] = m;
          });
        } catch (_) {}
      }

      const optionIndex = variantsUi
        ? parseInt(variantsUi.dataset.optionIndex, 10)
        : null;

      if (variantsUi && optionIndex != null) {
        variantsUi.querySelectorAll('[data-bsc-variant-option]').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.selectVariantOption(card, btn, variants, optionIndex, mediaMap);
          });
        });
      }
    }

    selectVariantOption(card, btn, variants, optionIndex, mediaMap) {
      const value = btn.dataset.value;
      const ui = card.querySelector('[data-bsc-variants-ui]');

      const currentSelections = this.getCurrentSelections(card, variants, optionIndex);
      currentSelections[optionIndex] = value;

      const variant = this.findVariant(variants, currentSelections);
      if (!variant) return;

      ui.querySelectorAll('[data-bsc-variant-option]').forEach((el) => {
        el.classList.toggle('is-active', el === btn);
        el.setAttribute('aria-pressed', el === btn ? 'true' : 'false');
      });

      this.applyVariant(card, variant, mediaMap);
    }

    getCurrentSelections(card, variants, changedIndex) {
      const ui = card.querySelector('[data-bsc-variants-ui]');
      const activeBtn = ui?.querySelector('.is-active');
      const input = card.querySelector('[data-bsc-variant-input]');
      const currentId = input ? parseInt(input.value, 10) : null;
      const current = variants.find((v) => v.id === currentId) || variants[0];

      const selections = [...current.options];

      if (activeBtn && changedIndex != null) {
        selections[changedIndex] = activeBtn.dataset.value;
      }

      return selections;
    }

    findVariant(variants, selections) {
      return variants.find((v) =>
        v.options.every((opt, i) => opt === selections[i])
      );
    }

    applyVariant(card, variant, mediaMap) {
      const input = card.querySelector('[data-bsc-variant-input]');
      const priceCurrent = card.querySelector('[data-bsc-price-current]');
      const priceCompare = card.querySelector('[data-bsc-price-compare]');
      const atcBtn = card.querySelector('[data-bsc-atc-btn]');
      const atcLabel = card.querySelector('.bsc-card__atc-label');
      const primaryImg = card.querySelector('[data-bsc-image-primary]');

      if (input) input.value = variant.id;

      if (priceCurrent) {
        priceCurrent.textContent = formatMoney(variant.price);
      }

      if (priceCompare) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceCompare.textContent = formatMoney(variant.compare_at_price);
          priceCompare.hidden = false;
        } else {
          priceCompare.hidden = true;
        }
      }

      if (atcBtn) {
        atcBtn.disabled = !variant.available;
      }

      if (atcLabel && atcBtn) {
        atcLabel.textContent = variant.available
          ? atcLabel.dataset.defaultText || atcLabel.textContent
          : 'Sold out';
      }

      const featuredMediaId = variant.featured_image?.id || variant.featured_media?.id;
      if (featuredMediaId && primaryImg && mediaMap[featuredMediaId]) {
        const media = mediaMap[featuredMediaId];
        primaryImg.src = media.src;
        if (media.srcset) primaryImg.srcset = media.srcset;
        if (media.alt) primaryImg.alt = media.alt;
      }
    }

    setupInfiniteClones() {
      if (!this.isCarouselActive()) return;

      const slides = this.originalSlides;
      if (slides.length < 2) return;

      this.cloneCount = Math.min(slides.length, 6);

      const prepClone = (node) => {
        const clone = node.cloneNode(true);
        clone.setAttribute('data-clone', 'true');
        clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        clone.querySelectorAll('[data-bsc-card]').forEach((card) => {
          this.initCard(card);
        });
        return clone;
      };

      for (let i = slides.length - this.cloneCount; i < slides.length; i++) {
        this.track.insertBefore(prepClone(slides[i]), this.track.firstChild);
      }
      for (let i = 0; i < this.cloneCount; i++) {
        this.track.appendChild(prepClone(slides[i]));
      }
    }

    recenter(instant = false) {
      if (!this._carouselActive || !this.infinite || !this.cloneCount) return;
      const firstOriginal = this.track.children[this.cloneCount];
      if (!firstOriginal) return;
      const behavior = this.viewport.style.scrollBehavior;
      this.viewport.style.scrollBehavior = 'auto';
      this.viewport.scrollLeft = firstOriginal.offsetLeft - this.getPeek();
      if (!instant) void this.viewport.offsetWidth;
      this.viewport.style.scrollBehavior = behavior;
    }

    getPeek() {
      const cs = getComputedStyle(this.viewport);
      return parseFloat(cs.paddingLeft) || 0;
    }

    getSlideWidth() {
      const slide = this.track.children[0];
      if (!slide) return 0;
      const style = getComputedStyle(this.track);
      const gap = parseFloat(style.columnGap || style.gap) || 0;
      return slide.getBoundingClientRect().width + gap;
    }

    bindArrows() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.scrollByCol(-1));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.scrollByCol(1));
      }
    }

    scrollByCol(direction) {
      if (!this.isCarouselActive()) return;
      const slideW = this.getSlideWidth();
      if (!slideW) return;
      this.viewport.scrollBy({ left: slideW * direction, behavior: 'smooth' });
    }

    bindScroll() {
      let raf;
      this.viewport.addEventListener('scroll', () => {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.handleScroll());
      });
    }

    handleScroll() {
      if (!this.isCarouselActive()) return;

      if (this.infinite && this.cloneCount) {
        const slideW = this.getSlideWidth();
        if (!slideW) return;
        const scrollLeft = this.viewport.scrollLeft;
        const originalLen = this.originalSlides.length;

        const firstRealLeft = this.track.children[this.cloneCount].offsetLeft - this.getPeek();
        const lastRealLeft = this.track.children[this.cloneCount + originalLen - 1].offsetLeft - this.getPeek();

        if (scrollLeft < firstRealLeft - slideW * 0.5) {
          const delta = firstRealLeft - scrollLeft;
          this.jumpTo(lastRealLeft + slideW - delta);
        } else if (scrollLeft > lastRealLeft + slideW * 0.5) {
          const delta = scrollLeft - (lastRealLeft + slideW);
          this.jumpTo(firstRealLeft + delta);
        }
      } else {
        this.updateArrowState();
      }
    }

    jumpTo(position) {
      const behavior = this.viewport.style.scrollBehavior;
      this.viewport.style.scrollBehavior = 'auto';
      this.viewport.scrollLeft = position;
      this.viewport.style.scrollBehavior = behavior;
    }

    updateArrowState() {
      if (!this.isCarouselActive()) return;
      if (this.infinite) return;
      if (!this.prevBtn || !this.nextBtn) return;
      const sl = this.viewport.scrollLeft;
      const max = this.viewport.scrollWidth - this.viewport.clientWidth;
      this.prevBtn.disabled = sl <= 1;
      this.nextBtn.disabled = sl >= max - 1;
    }

    bindDrag() {
      const DRAG_THRESHOLD = 8;

      let pointerDown = false;
      let isDragging = false;
      let startX = 0;
      let startScroll = 0;
      let pointerId = null;

      const onDown = (e) => {
        if (!this.isCarouselActive()) return;
        if (e.pointerType !== 'mouse') return;
        if (e.button !== 0) return;
        pointerDown = true;
        isDragging = false;
        startX = e.clientX;
        startScroll = this.viewport.scrollLeft;
        pointerId = e.pointerId;
      };

      const onMove = (e) => {
        if (!pointerDown) return;
        const dx = e.clientX - startX;

        if (!isDragging) {
          if (Math.abs(dx) <= DRAG_THRESHOLD) return;
          isDragging = true;
          this.viewport.classList.add('is-dragging');
          try { this.viewport.setPointerCapture(pointerId); } catch (_) {}
        }

        this.viewport.scrollLeft = startScroll - dx;
        e.preventDefault();
      };

      const onUp = () => {
        if (!pointerDown) return;
        const wasDragging = isDragging;
        pointerDown = false;
        isDragging = false;
        this.viewport.classList.remove('is-dragging');
        if (pointerId != null) {
          try { this.viewport.releasePointerCapture(pointerId); } catch (_) {}
          pointerId = null;
        }
        if (wasDragging) {
          this.viewport.classList.add('was-dragging');
          setTimeout(() => this.viewport.classList.remove('was-dragging'), 60);
          this.snapToNearest();
        }
      };

      this.viewport.addEventListener('pointerdown', onDown);
      this.viewport.addEventListener('pointermove', onMove);
      this.viewport.addEventListener('pointerup', onUp);
      this.viewport.addEventListener('pointercancel', onUp);

      this.viewport.addEventListener('click', (e) => {
        if (this.viewport.classList.contains('was-dragging')) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);

      this.viewport.addEventListener('dragstart', (e) => e.preventDefault());
    }

    snapToNearest() {
      if (!this.isCarouselActive()) return;
      const slideW = this.getSlideWidth();
      if (!slideW) return;
      const peek = this.getPeek();
      const sl = this.viewport.scrollLeft;
      const idx = Math.round((sl + peek) / slideW);
      const target = idx * slideW - peek;
      this.viewport.scrollTo({ left: target, behavior: 'smooth' });
    }

    bindAddToCart() {
      this.querySelectorAll('form.bsc-form').forEach((form) => {
        const label = form.querySelector('.bsc-card__atc-label');
        if (label && !label.dataset.defaultText) {
          label.dataset.defaultText = label.textContent.trim();
        }

        form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.submitAddToCart(form);
        });
      });
    }

    async submitAddToCart(form) {
      const button = form.querySelector('.bsc-card__atc');
      if (!button || button.disabled) return;

      button.classList.add('is-loading');

      const cart =
        document.querySelector('cart-notification') ||
        document.querySelector('cart-drawer');

      const config = typeof fetchConfig === 'function'
        ? fetchConfig('javascript')
        : { method: 'POST', headers: {} };

      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(form);
      if (cart && typeof cart.getSectionsToRender === 'function') {
        formData.append(
          'sections',
          cart.getSectionsToRender().map((s) => s.id)
        );
        formData.append('sections_url', window.location.pathname);
        if (typeof cart.setActiveElement === 'function') {
          cart.setActiveElement(document.activeElement);
        }
      }
      config.body = formData;

      try {
        const base = (window.routes && window.routes.cart_add_url) || '/cart/add';
        const response = await fetch(base, config);
        const data = await response.json();

        if (data.status) {
          this.handleErrorMessage(form, data.description || data.message);
          return;
        }

        if (!cart) {
          window.location = (window.routes && window.routes.cart_url) || '/cart';
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'bestseller-carousel',
            productVariantId: formData.get('id'),
          });
        }

        cart.renderContents(data);
      } catch (err) {
        console.error('[bestseller-carousel] ATC failed', err);
      } finally {
        button.classList.remove('is-loading');
      }
    }

    handleErrorMessage(form, message) {
      const wrapper = form.querySelector('.bsc-card__atc-error');
      const textEl = form.querySelector('.bsc-card__atc-error-text');
      if (!wrapper || !textEl) return;
      if (message) {
        wrapper.hidden = false;
        textEl.textContent = message;
      } else {
        wrapper.hidden = true;
        textEl.textContent = '';
      }
    }

    debounce(fn, wait) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }

  customElements.define('bestseller-carousel', BestsellerCarousel);
})();
